import React from 'react';
import { Title, PasswordTextField } from '../../elements';
import { UserService } from '../../hooks/UserService';
import { notifications, getLoginErrorMessage, parseAccountLockout, AccountLockoutInfo } from '../../utils';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  Container,
  Divider,
  Icon,
  InputAdornment,
  Paper,
  TextField
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SyncTwoToneIcon from '@mui/icons-material/SyncTwoTone';

import useAuthContext from '../../hooks/useAuthContext';
import { useTranslation } from 'react-i18next';
import { getAuthValueFromToken } from '../../utils/tokenHelper';
import { brand } from '../../theme/brand';
import { V4_OUTLINED_DEFAULT } from '../../theme/v4Compat';

const Login: React.FC<any> = () => {
  const userService = new UserService();

  const { t, i18n } = useTranslation();
  const { state, updateToken, updateRefreshToken } = useAuthContext();
  const navigate = useNavigate();

  // farm login or veterinary login
  const [isFarmLogin, setIsFarmLogin] = React.useState(true);

  const [number, setNumber] = React.useState('');
  const [isBusyLogin, setIsBusyLogin] = React.useState(false);
  const [userPassword, setUserPassword] = React.useState('');
  const [userEmail, setUserEmail] = React.useState('');

  const [lockout, setLockout] = React.useState<AccountLockoutInfo | null>(null);

  const [numberValidation, setNumberValidation] = React.useState(false);
  const [emailValidation, setEmailValidation] = React.useState(false);
  const [passValidation, setPassValidation] = React.useState(false);

  const cleanValidation = () => {
    setNumberValidation(false);
    setEmailValidation(false);
    setPassValidation(false);
  };

  const isValidForm = (): boolean => {
    cleanValidation();
    let isValid = true;

    if (!number) {
      notifications.warning(isFarmLogin ? 'Missing unique farm number' : 'Missing veterinary number');
      setNumberValidation(true);
      isValid = false;
    }
    if (!userEmail) {
      notifications.warning('Missing user email address');
      setEmailValidation(true);
      isValid = false;
    }
    if (!userPassword) {
      notifications.warning('Missing user password');
      setPassValidation(true);
      isValid = false;
    }

    return isValid;
  };

  const proceedAuth = async () => {
    if (isFarmLogin) {
      await authFarm();
    } else {
      await authVeterinary();
    }
  };

  const handleLoginError = (e: unknown) => {
    const lockoutInfo = parseAccountLockout(e);
    if (lockoutInfo.isLocked) {
      setLockout(lockoutInfo);
    } else {
      setLockout(null);
      notifications.error(getLoginErrorMessage(e, t));
    }
    setIsBusyLogin(false);
  };

  const authFarm = async () => {
    if (!isValidForm()) {
      return;
    }

    setIsBusyLogin(true);

    await userService
      .authUser(number, userEmail, userPassword)
      .then(async (auth) => {
        notifications.success('Success user login!');
        setLockout(null);

        updateToken(auth.token);
        updateRefreshToken(auth.refreshToken);
        i18n.changeLanguage(getAuthValueFromToken(auth.token).locale);

        setIsBusyLogin(false);
      })
      .catch(handleLoginError);
  };

  const toggleLoginMode = async () => {
    setIsFarmLogin(!isFarmLogin);
  };

  const handleAvatarClick = async () => {
    await userService.getConfig().then((result) => console.info(result));
  };

  const authVeterinary = async () => {
    if (!isValidForm()) {
      return;
    }

    setIsBusyLogin(true);

    await userService
      .authVeterinary(number, userEmail, userPassword)
      .then(async (auth) => {
        notifications.success('Success veterinary login!');
        setLockout(null);
        setIsBusyLogin(false);

        updateToken(auth.token);
        updateRefreshToken(auth.refreshToken);
        i18n.changeLanguage(getAuthValueFromToken(auth.token).locale);
      })
      .catch(handleLoginError);
  };

  const handleHiddenClick = () => {
    navigate('/admin');
  };

  return state.isLoggedIn ? (
    <Navigate to={{ pathname: '/dashboard' }} />
  ) : (
    // The widths are v4's (lg 1280px; sm 600px, md 960px), which this page was laid out against - v9 moved
    // lg and md, and spelling them out keeps the card where it was.
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <div className="-m-3 flex flex-wrap justify-center">
        <div className="box-border w-full p-3 min-[600px]:w-8/12 min-[960px]:w-6/12">
          <Paper
            elevation={0}
            sx={{ p: 3, display: 'flex', overflow: 'auto', flexDirection: 'column', minHeight: 375 }}
          >
            {/* login header */}
            <div className="-mb-1 flex flex-nowrap items-center gap-2">
              <Avatar alt={brand.logoAlt} onClick={handleAvatarClick} src={brand.logoSrc} />
              <Title>{`${brand.appName} ${isFarmLogin ? 'Farm' : 'Veterinary'} Login`}</Title>
              <Button
                onClick={handleHiddenClick}
                variant="text"
                size="small"
                color="primary"
                style={{
                  opacity: 0,
                  pointerEvents: 'auto',
                  backgroundColor: 'transparent'
                }}
              >
                Login
              </Button>
            </div>
            {/* login header */}
            <hr />

            {lockout && (
              <Alert severity="warning" id="loginLockoutBanner" className="!mb-3">
                <div>
                  {lockout.until
                    ? t('f-login-locked-until', {
                        until: lockout.until.toLocaleString(i18n.language || undefined),
                        interpolation: { escapeValue: false }
                      })
                    : t('f-login-locked')}
                </div>
                <Button
                  id="lockoutForgotPasswordLink"
                  variant="text"
                  size="small"
                  color="primary"
                  className="!p-0 !mt-1"
                  onClick={() => navigate('/forgot-password')}
                >
                  {t('f-login-locked-hint')}
                </Button>
              </Alert>
            )}

            {/*login form */}
            <div className="flex justify-center">
              <div className="box-border flex min-h-[250px] w-full flex-col justify-center min-[600px]:w-7/12">
                <TextField
                  variant="outlined"
                  // v4's dense margin also made the input dense; v9 splits that out into size.
                  size="small"
                  margin="dense"
                  fullWidth
                  value={number}
                  onChange={(e) => {
                    setNumber(e.target.value);
                  }}
                  id="number"
                  label={isFarmLogin ? 'Unique farm number' : 'Unique veterinary number'}
                  helperText="Required"
                  error={numberValidation}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          {isFarmLogin ? <Icon>{'home_work'}</Icon> : <Icon>{'medical_services'}</Icon>}
                        </InputAdornment>
                      )
                    }
                  }}
                />

                <TextField
                  variant="outlined"
                  // v4's dense margin also made the input dense; v9 splits that out into size.
                  size="small"
                  type="email"
                  margin="dense"
                  fullWidth
                  value={userEmail}
                  onChange={(e) => {
                    setUserEmail(e.target.value);
                  }}
                  id="userEmail"
                  label="User email"
                  helperText="Required"
                  error={emailValidation}
                />

                <PasswordTextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={userPassword}
                  onChange={(e) => {
                    setUserPassword(e.target.value);
                  }}
                  id="userPass"
                  label="Password"
                  helperText="Required"
                  error={passValidation}
                />

                <Button
                  id="userLoginBtn"
                  startIcon={<LockOpenIcon />}
                  sx={{ mt: 1 }}
                  disabled={isBusyLogin}
                  variant="contained"
                  size="small"
                  color="primary"
                  fullWidth
                  type="submit"
                  onClick={proceedAuth}
                >
                  {'Login'}
                </Button>

                <Divider className="!my-[15px]" />

                <div className="my-[15px] flex justify-center">
                  <Button
                    id="switcModeBtn"
                    startIcon={<SyncTwoToneIcon />}
                    disabled={isBusyLogin}
                    variant="outlined"
                    size="small"
                    color="inherit"
                    sx={V4_OUTLINED_DEFAULT}
                    onClick={() => {
                      cleanValidation();
                      toggleLoginMode();
                    }}
                  >
                    {isFarmLogin ? 'Switch to veterinary login' : 'Switch to farm login'}
                  </Button>
                </div>

                <div className="flex justify-center">
                  <Button
                    id="forgotPasswordLink"
                    variant="text"
                    size="small"
                    color="primary"
                    onClick={() => navigate('/forgot-password')}
                  >
                    {'Forgot password?'}
                  </Button>
                </div>
              </div>
            </div>
            {/* Login form */}
            <div className="flex justify-center">{isBusyLogin === true && <CircularProgress size={72} />}</div>
          </Paper>
        </div>
      </div>
    </Container>
  );
};

export default Login;
